import { JSONContent } from '@tiptap/core'

import { ContentBlockish, RawContentish } from './bullets'

export * from './bullets'

const withDom = <T>(html: string, fn: (root: HTMLElement) => T): T => {
  const root = document.createElement('div')
  root.innerHTML = html || ''
  return fn(root)
}

export const htmlToPlainText = (html: string): string => withDom(html, root => root.textContent || '')

export const htmlHasText = (html: string): boolean => htmlToPlainText(html).trim().length > 0

/**
 * Blocks sending while unreplaced template placeholders remain.
 * Previously `editorStateHasPlaceholderText`.
 */
export const htmlHasPlaceholder = (html: string): boolean =>
  withDom(html, root => root.querySelector('span.placeholder') !== null)

/**
 * Prepares editor HTML for the API.
 *
 * The account-page URL is neutralised to href="#" inside the editor and the
 * real target parked in data-hidden-href; restore it here. When `disableLinks`
 * is set the anchor is unwrapped entirely, matching the old
 * `stateToHTMLOptions({ disableLinks: true })` behaviour.
 */
export const serializeForSend = (html: string, { disableLinks = false } = {}): string =>
  withDom(html, root => {
    root.querySelectorAll('a.account-page-url').forEach(anchor => {
      const hidden = anchor.getAttribute('data-hidden-href')
      if (disableLinks) {
        anchor.replaceWith(...Array.from(anchor.childNodes))
        return
      }
      if (hidden) {
        anchor.setAttribute('href', hidden)
      }
      anchor.removeAttribute('data-hidden-href')
    })
    return root.innerHTML
  })

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * Blocks -> HTML, nesting unordered lists by depth so the editor renders the
 * same structure the plain-text form describes.
 */
export const blocksToHTML = (raw: RawContentish): string => {
  const out: string[] = []
  let openList: 'ul' | 'ol' | null = null
  let openDepth = 0

  const closeTo = (depth: number) => {
    while (openList && openDepth > depth) {
      out.push(`</li></${openList}>`)
      openDepth -= 1
    }
  }
  const closeAll = () => {
    while (openList && openDepth >= 0) {
      out.push(`</li></${openList}>`)
      openDepth -= 1
      if (openDepth < 0) break
    }
    openList = null
    openDepth = 0
  }

  raw.blocks.forEach(block => {
    const text = escapeHtml(block.text)
    const listTag = block.type === 'ordered-list-item' ? 'ol' : block.type === 'unordered-list-item' ? 'ul' : null

    if (!listTag) {
      closeAll()
      out.push(`<p>${text || '<br>'}</p>`)
      return
    }

    const depth = Math.max(0, block.depth || 0)
    if (!openList) {
      openList = listTag
      openDepth = 0
      out.push(`<${listTag}><li><p>${text}</p>`)
      for (let d = 0; d < depth; d++) {
        out.push(`<${listTag}><li><p></p>`)
        openDepth += 1
      }
      return
    }
    if (depth > openDepth) {
      for (let d = openDepth; d < depth; d++) {
        out.push(`<${listTag}><li><p>${d === depth - 1 ? text : ''}</p>`)
        openDepth += 1
      }
      return
    }
    closeTo(depth)
    out.push(`</li><li><p>${text}</p>`)
  })

  closeAll()
  return out.join('')
}

/**
 * TipTap doc -> blocks, the inverse of blocksToHTML. Depth is the list nesting
 * level so `toPlainTextWithBullets` can pick the right bullet glyph.
 */
export const docToBlocks = (doc: JSONContent): RawContentish => {
  const blocks: ContentBlockish[] = []

  const textOf = (node: JSONContent): string => {
    if (node.type === 'text') return node.text || ''
    if (node.type === 'hardBreak') return '\n'
    return (node.content || []).map(textOf).join('')
  }

  const walk = (node: JSONContent, listType: string | null, depth: number) => {
    switch (node.type) {
      case 'bulletList':
      case 'orderedList': {
        const type = node.type === 'bulletList' ? 'unordered-list-item' : 'ordered-list-item'
        ;(node.content || []).forEach(item => walk(item, type, depth))
        return
      }
      case 'listItem': {
        const children = node.content || []
        const paragraphs = children.filter(c => c.type === 'paragraph')
        const nested = children.filter(c => c.type === 'bulletList' || c.type === 'orderedList')
        blocks.push({
          text: paragraphs.map(textOf).join(' '),
          type: listType || 'unordered-list-item',
          depth,
        })
        nested.forEach(n => walk(n, listType, depth + 1))
        return
      }
      case 'paragraph': {
        blocks.push({ text: textOf(node), type: 'unstyled', depth: 0 })
        return
      }
      default:
        ;(node.content || []).forEach(child => walk(child, listType, depth))
    }
  }

  ;(doc.content || []).forEach(node => walk(node, null, 0))
  return { blocks }
}
